import { AffiliateCode } from "@/constants/affiliateCode"
import { AffiliateOrganisation } from "@/constants/affiliateOrganisation"
import { AccountCampaignSlug } from "@/entities/accountCampaign"
import { ProviderCalendarId } from "@/entities/providerCalendar"
import { type InputMethod } from "@/features/subscription/meterEligibility/types/inputMethod"
import { getDefaultInputMethodFor } from "@/features/subscription/meterEligibility/utils/getDefaultInputMethodFor"
import { type JourneyType } from "@/scalars/journeyType"
import { type SalesChannelWithTelesales } from "@/scalars/salesChannelChoices"
import { type SubscriptionType } from "../../schemas/subscriptionType"
import { SubscriptionFunnelType } from "../../types/funnelType"

const DEFAULT_JOURNEY_TYPE_CHANGE_OF_SUPPLIER: JourneyType = "CHANGE_OF_SUPPLIER"

export type CustomParamsFormValues = {
    accountCampaignSlug?: AccountCampaignSlug
    availableFunnelTypes?: SubscriptionFunnelType[]
    availableJourneyTypes?: JourneyType[]
    invoiceUploadEnabled?: boolean
    journeyType?: JourneyType
    inputMethod?: InputMethod
    withdrawableJourneyType?: JourneyType[]
    providerCalendarForced?: ProviderCalendarId
    hasElectricVehicle?: boolean
    hasElectricVehicleToggle?: boolean
    meterPowerAnalysisEnabled?: boolean
    subscriptionType?: SubscriptionType
    hasCPCQuoting?: boolean
    canEditEstimatedConsumption?: boolean
    hasPowerMeterSelect?: boolean
}

export const SALES_CHANNEL_CUSTOM_PARAMS = {
    DIRECT: {
        availableFunnelTypes: [
            SubscriptionFunnelType.MonoEnergyElectricity,
            SubscriptionFunnelType.MonoEnergyGas,
            SubscriptionFunnelType.DualEnergy,
        ],
        availableJourneyTypes: ["CHANGE_OF_SUPPLIER", "COMMISSIONING"],
        invoiceUploadEnabled: true,
        journeyType: DEFAULT_JOURNEY_TYPE_CHANGE_OF_SUPPLIER,
        inputMethod: getDefaultInputMethodFor(DEFAULT_JOURNEY_TYPE_CHANGE_OF_SUPPLIER),
        withdrawableJourneyType: [],
        meterPowerAnalysisEnabled: true,
        hasCPCQuoting: false,
        canEditEstimatedConsumption: false,
    },
    FIELD_SALES: {
        availableFunnelTypes: [
            SubscriptionFunnelType.MonoEnergyElectricity,
            SubscriptionFunnelType.MonoEnergyGas,
            SubscriptionFunnelType.DualEnergy,
        ],
        availableJourneyTypes: ["CHANGE_OF_SUPPLIER", "COMMISSIONING"],
        invoiceUploadEnabled: false,
        journeyType: DEFAULT_JOURNEY_TYPE_CHANGE_OF_SUPPLIER,
        inputMethod: getDefaultInputMethodFor(DEFAULT_JOURNEY_TYPE_CHANGE_OF_SUPPLIER),
        subscriptionType: "NAVIGATOR",
        withdrawableJourneyType: ["CHANGE_OF_SUPPLIER", "COMMISSIONING"],
        meterPowerAnalysisEnabled: true,
        hasCPCQuoting: false,
        canEditEstimatedConsumption: true,
    },
    PRICE_COMPARISON: {
        availableFunnelTypes: [
            SubscriptionFunnelType.MonoEnergyElectricity,
            SubscriptionFunnelType.MonoEnergyGas,
            SubscriptionFunnelType.DualEnergy,
        ],
        availableJourneyTypes: ["CHANGE_OF_SUPPLIER", "COMMISSIONING"],
        invoiceUploadEnabled: false,
        journeyType: DEFAULT_JOURNEY_TYPE_CHANGE_OF_SUPPLIER,
        inputMethod: getDefaultInputMethodFor(DEFAULT_JOURNEY_TYPE_CHANGE_OF_SUPPLIER),
        subscriptionType: "NAVIGATOR",
        withdrawableJourneyType: [],
        // There is a Backend check for PRICE_COMPARISON to not be able to change the max power
        // https://github.com/octoenergy/kraken-core/blob/ff0c75b650df93d8eb3de4781e5c4b4d3c6ef595/src/octoenergy/plugins/territories/fra/interfaces/apisite/graphql/quoting/types.py#L321
        meterPowerAnalysisEnabled: false,
        hasCPCQuoting: false,
        canEditEstimatedConsumption: false,
    },
    SALES_TENDER: {
        availableFunnelTypes: [
            SubscriptionFunnelType.MonoEnergyElectricity,
            SubscriptionFunnelType.MonoEnergyGas,
            SubscriptionFunnelType.DualEnergy,
        ],
        availableJourneyTypes: ["CHANGE_OF_SUPPLIER", "COMMISSIONING"],
        invoiceUploadEnabled: false,
        journeyType: DEFAULT_JOURNEY_TYPE_CHANGE_OF_SUPPLIER,
        inputMethod: getDefaultInputMethodFor(DEFAULT_JOURNEY_TYPE_CHANGE_OF_SUPPLIER),
        withdrawableJourneyType: [],
        meterPowerAnalysisEnabled: true,
        hasCPCQuoting: false,
        canEditEstimatedConsumption: false,
    },
    WORKPLACE_POP_UP: {
        availableFunnelTypes: [
            SubscriptionFunnelType.MonoEnergyElectricity,
            SubscriptionFunnelType.MonoEnergyGas,
            SubscriptionFunnelType.DualEnergy,
        ],
        availableJourneyTypes: ["CHANGE_OF_SUPPLIER", "COMMISSIONING"],
        invoiceUploadEnabled: false,
        journeyType: DEFAULT_JOURNEY_TYPE_CHANGE_OF_SUPPLIER,
        inputMethod: getDefaultInputMethodFor(DEFAULT_JOURNEY_TYPE_CHANGE_OF_SUPPLIER),
        subscriptionType: "NAVIGATOR",
        withdrawableJourneyType: ["CHANGE_OF_SUPPLIER", "COMMISSIONING"],
        meterPowerAnalysisEnabled: true,
        hasCPCQuoting: false,
        canEditEstimatedConsumption: true,
    },
    TELESALES: {
        availableFunnelTypes: [
            SubscriptionFunnelType.MonoEnergyElectricity,
            SubscriptionFunnelType.MonoEnergyGas,
            SubscriptionFunnelType.DualEnergy,
        ],
        availableJourneyTypes: ["CHANGE_OF_SUPPLIER", "COMMISSIONING"],
        invoiceUploadEnabled: false,
        journeyType: DEFAULT_JOURNEY_TYPE_CHANGE_OF_SUPPLIER,
        inputMethod: getDefaultInputMethodFor(DEFAULT_JOURNEY_TYPE_CHANGE_OF_SUPPLIER),
        subscriptionType: "NAVIGATOR",
        withdrawableJourneyType: [],
        meterPowerAnalysisEnabled: true,
        hasCPCQuoting: false,
        canEditEstimatedConsumption: true,
    },
} as const satisfies Record<SalesChannelWithTelesales, CustomParamsFormValues>

export const AFFILIATE_LINK_CUSTOM_PARAMS = {
    [AffiliateCode.OCTOPUS_DIRECT]: {
        hasCPCQuoting: true,
    },
    [AffiliateCode.OCTOFLEX]: {
        availableFunnelTypes: [
            SubscriptionFunnelType.MonoEnergyElectricity,
            SubscriptionFunnelType.DualEnergy,
        ],
        hasElectricVehicle: false,
        hasElectricVehicleToggle: false,
    },
    // Affiliate Octopus Go
    [AffiliateCode.OCTOPUS_GO]: {
        availableFunnelTypes: [
            SubscriptionFunnelType.MonoEnergyElectricity,
            SubscriptionFunnelType.DualEnergy,
        ],
        providerCalendarForced: ProviderCalendarId.HPHC,
        hasElectricVehicle: true,
        hasElectricVehicleToggle: false,
    },
    [AffiliateCode.ENKI_OCTOPUS_GO]: {
        availableFunnelTypes: [
            SubscriptionFunnelType.MonoEnergyElectricity,
            SubscriptionFunnelType.DualEnergy,
        ],
        providerCalendarForced: ProviderCalendarId.HPHC,
        hasElectricVehicle: true,
        hasElectricVehicleToggle: false,
    },
    // Affiliate Intelligent Octopus
    [AffiliateCode.INTELLIGENT_OCTOPUS]: {
        availableFunnelTypes: [
            SubscriptionFunnelType.MonoEnergyElectricity,
            SubscriptionFunnelType.DualEnergy,
        ],
        hasElectricVehicle: true,
        hasElectricVehicleToggle: false,
    },
    [AffiliateCode.ENKI_INTELLIGENT_OCTOPUS]: {
        availableFunnelTypes: [
            SubscriptionFunnelType.MonoEnergyElectricity,
            SubscriptionFunnelType.DualEnergy,
        ],
        hasElectricVehicle: true,
        hasElectricVehicleToggle: false,
    },
    [AffiliateCode.RENAULT_INTELLIGENT_OCTOPUS]: {
        availableFunnelTypes: [
            SubscriptionFunnelType.MonoEnergyElectricity,
            SubscriptionFunnelType.DualEnergy,
        ],
        hasElectricVehicle: true,
        hasElectricVehicleToggle: false,
    },
    [AffiliateCode.FORD_1_YEAR]: {
        availableFunnelTypes: [
            SubscriptionFunnelType.MonoEnergyElectricity,
            SubscriptionFunnelType.DualEnergy,
        ],
        accountCampaignSlug: AccountCampaignSlug.Ford1Year,
        hasElectricVehicle: true,
        hasElectricVehicleToggle: false,
    },
    [AffiliateCode.FORD_3_YEARS]: {
        availableFunnelTypes: [
            SubscriptionFunnelType.MonoEnergyElectricity,
            SubscriptionFunnelType.DualEnergy,
        ],
        accountCampaignSlug: AccountCampaignSlug.Ford3Years,
        hasElectricVehicle: true,
        hasElectricVehicleToggle: false,
    },
    [AffiliateCode.OFFRE_EFFY]: {
        hasCPCQuoting: true,
        hasPowerMeterSelect: true,
        availableFunnelTypes: [SubscriptionFunnelType.MonoEnergyElectricity],
        meterPowerAnalysisEnabled: false,
    },
} as const satisfies Partial<Record<AffiliateCode, CustomParamsFormValues>>

export const AFFILIATE_ORGANISATION_CUSTOM_PARAMS = {
    [AffiliateOrganisation.FNAC_DARTY_DARTY]: {
        availableFunnelTypes: [
            SubscriptionFunnelType.MonoEnergyElectricity,
            SubscriptionFunnelType.DualEnergy,
        ],
        availableJourneyTypes: ["CHANGE_OF_SUPPLIER"],
    },
    [AffiliateOrganisation.FNAC_DARTY_OCTOPUS]: {
        availableFunnelTypes: [
            SubscriptionFunnelType.MonoEnergyElectricity,
            SubscriptionFunnelType.DualEnergy,
        ],
        availableJourneyTypes: ["CHANGE_OF_SUPPLIER"],
    },
    [AffiliateOrganisation.FNAC_DARTY_ENERCHOICE]: {
        availableFunnelTypes: [
            SubscriptionFunnelType.MonoEnergyElectricity,
            SubscriptionFunnelType.DualEnergy,
        ],
        availableJourneyTypes: ["CHANGE_OF_SUPPLIER"],
    },
    [AffiliateOrganisation.FNAC_DARTY_JOULERSERVE]: {
        availableFunnelTypes: [
            SubscriptionFunnelType.MonoEnergyElectricity,
            SubscriptionFunnelType.DualEnergy,
        ],
        availableJourneyTypes: ["CHANGE_OF_SUPPLIER"],
    },
    [AffiliateOrganisation.CREDIT_MUTUEL]: {
        availableFunnelTypes: [
            SubscriptionFunnelType.MonoEnergyElectricity,
            SubscriptionFunnelType.DualEnergy,
        ],
        availableJourneyTypes: ["CHANGE_OF_SUPPLIER"],
    },
} as const satisfies Partial<Record<AffiliateOrganisation, CustomParamsFormValues>>
